// ============================================================================
// WEB PUSH, FROM THE PAGE'S SIDE — the half of NOTIF-1b that is not a service worker.
//
// THREE THINGS LIVE HERE, and they are here together because they are the same story told to
// three different audiences:
//
//   1. WHAT THIS BROWSER CAN DO — `pushSupported`, `currentPermission`. Read, never remembered:
//      the owner can change a site permission in the browser's own settings while the tab is
//      open, and a cached answer would be a claim about a permission rather than a reading of it.
//   2. SUBSCRIBING AND UNSUBSCRIBING — the browser prompt, `pushManager.subscribe`, and the POST
//      that tells the Worker where to send. The middle step is the only part of this feature
//      that is not a fetch, which is why it is not in remote.ts.
//   3. THE HANDOFF WITH public/push-sw.js — the VAPID key this file leaves in the Cache API for a
//      woken service worker, and the re-subscription that worker leaves for this file. See
//      `flushPendingResubscription`; the service worker's header says why it cannot POST.
//
// WHY THE API ARRIVES AS AN ARGUMENT. lib/data/index.ts imports this file to build its hooks, so
// importing `data` back from here would be a cycle. `PushApi` is the four methods this file
// actually uses, and `DataAPI` satisfies it structurally — demo and remote alike.
//
// NOTHING HERE INVENTS A LABEL. The Worker's rule (`toPushSubscription`) is that a device is
// called what the owner called it or nothing at all; the user agent is sent alongside as a
// separate fact, and the screen shows it as one.
// ============================================================================

import type { PushDevice, PushSubscribeInput } from './data/types'

/** Bumping any of these means bumping them in apps/web/public/push-sw.js too. */
export const PUSH_CACHE = 'lifeos-push'
/** Where this file leaves the VAPID key so a woken service worker can re-subscribe without it. */
export const PUSH_KEY_URL = '/__lifeos-push__/application-server-key'
/** Where the service worker leaves a subscription this file still has to tell the API about. */
export const PUSH_PENDING_URL = '/__lifeos-push__/pending-subscription'

/** The four data-contract methods this file needs. `DataAPI` satisfies it; so does a test stub. */
export interface PushApi {
  getPushPublicKey(): Promise<string>
  getPushSubscriptions(): Promise<PushDevice[]>
  subscribeToPush(input: PushSubscribeInput): Promise<PushDevice>
  unsubscribeFromPush(id: string): Promise<PushDevice>
}

/** `granted` · `denied` · `default` — or `unsupported`, which is its own answer, not a failure. */
export type PushPermission = 'granted' | 'denied' | 'default' | 'unsupported'

export const PERMISSION_DENIED_MESSAGE =
  'This browser is blocking notifications for LifeOS. Open the site settings — the padlock or ' +
  'the ⋮ menu beside the address bar — allow Notifications, then press Enable again.'

export const PERMISSION_DISMISSED_MESSAGE =
  'The browser prompt was closed without an answer, so nothing changed. Press Enable again when ' +
  'you are ready to allow it.'

export const UNSUPPORTED_MESSAGE =
  'This browser has no Web Push: it offers no service worker, no PushManager, or no Notification ' +
  'API. LifeOS can still show everything inside the app — it just cannot reach you when the app ' +
  'is closed. Chrome on Android and on the desktop can.'

// ── What this browser can do ────────────────────────────────────────────────

/**
 * ALL THREE PIECES, because any one of them missing means the same thing to the owner. Safari on
 * iOS has the APIs only for an installed web app; a page served over plain http has none of them.
 */
export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  )
}

/** Read at the moment it is asked for. Never cached — see the header. */
export function currentPermission(): PushPermission {
  if (typeof Notification === 'undefined') return 'unsupported'
  const permission = Notification.permission
  return permission === 'granted' || permission === 'denied' ? permission : 'default'
}

// ── base64url ───────────────────────────────────────────────────────────────

/**
 * The VAPID key as bytes. `applicationServerKey` takes a BufferSource, and the key the Worker
 * hands out is base64url — `atob` speaks base64, which is a different alphabet in exactly two
 * characters and in the padding. Getting this wrong produces a subscription the push service
 * accepts and the Worker can never sign for, which is a failure with no visible symptom until a
 * notification silently does not arrive.
 */
export function base64UrlToBytes(value: string) {
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=')
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  // Backed by a plain ArrayBuffer, stated rather than inferred: `new Uint8Array(n)` is typed over
  // `ArrayBufferLike`, which includes SharedArrayBuffer and therefore satisfies neither
  // `BufferSource` (what `applicationServerKey` takes) nor `BodyInit` (what the cache write takes).
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

// ── The state the screen draws ──────────────────────────────────────────────

export interface PushState {
  supported: boolean
  permission: PushPermission
  /** This browser's own push endpoint, or null when it holds no subscription. */
  endpoint: string | null
  /** The server row for THIS browser — matched on the endpoint, never on a guess. */
  device: PushDevice | null
  /** True only when this browser has a subscription AND the server knows about it, live. */
  enabled: boolean
}

/**
 * WHAT IS TRUE ABOUT THIS DEVICE, right now, from both sides.
 *
 * The two halves can disagree, and the disagreement is the interesting part: a browser holding a
 * subscription the server has never heard of (the API was down when Enable was pressed) is not
 * enabled, and neither is a browser whose row the server has disabled after a 410. `enabled`
 * requires both, so the switch cannot sit on while nothing would ever arrive.
 */
export async function subscriptionState(api: PushApi): Promise<PushState> {
  const permission = currentPermission()
  if (!pushSupported()) {
    return { supported: false, permission, endpoint: null, device: null, enabled: false }
  }
  const endpoint = await browserEndpoint()
  if (!endpoint) {
    return { supported: true, permission, endpoint: null, device: null, enabled: false }
  }
  const devices = await api.getPushSubscriptions()
  const device = devices.find((d) => d.endpoint === endpoint) ?? null
  return {
    supported: true,
    permission,
    endpoint,
    device,
    enabled: Boolean(device) && !device!.disabledAt,
  }
}

/** This browser's push endpoint, or null. Null is an answer — it is what "not subscribed" is. */
export async function browserEndpoint(): Promise<string | null> {
  if (!pushSupported()) return null
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return null
  const subscription = await registration.pushManager.getSubscription()
  return subscription?.endpoint ?? null
}

// ── Subscribing ─────────────────────────────────────────────────────────────

/**
 * TURN THIS DEVICE ON: ask, subscribe, tell the Worker. In that order, and every step can refuse
 * out loud.
 *
 * THE PERMISSION PROMPT COMES FIRST AND FROM A PRESS. `Notification.requestPermission()` is only
 * allowed to show a prompt in response to a user gesture, and a browser that has already been
 * told "no" resolves `denied` without showing anything — which is why the denied message below
 * explains where the browser's own switch is instead of suggesting another press.
 *
 * THE KEY IS CACHED ON THE WAY THROUGH, for public/push-sw.js: when the push service rotates this
 * browser's keys, the service worker is woken with no page, no session and no way to fetch the
 * key again. That copy is the only thing that lets it re-subscribe.
 */
export async function subscribe(api: PushApi, label?: string): Promise<PushDevice> {
  if (!pushSupported()) throw new Error(UNSUPPORTED_MESSAGE)

  const permission = await Notification.requestPermission()
  if (permission === 'denied') throw new Error(PERMISSION_DENIED_MESSAGE)
  if (permission !== 'granted') throw new Error(PERMISSION_DISMISSED_MESSAGE)

  // The Worker's sentence arrives here when the deployment has no VAPID keys. It is thrown as
  // written rather than translated — "push not configured" is the Worker's fact to state.
  const publicKey = await api.getPushPublicKey()
  const keyBytes = base64UrlToBytes(publicKey)

  const registration = await navigator.serviceWorker.ready
  // REUSE WHAT THIS BROWSER ALREADY HAS. Pressing Enable on a device that is already subscribed
  // must land on ONE row — and the POST below re-enables a row a 410 had disabled, which is
  // exactly what "press Enable again" on the screen promises.
  const existing = await registration.pushManager.getSubscription()
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes,
    }))

  await stashApplicationServerKey(keyBytes)

  const json = subscription.toJSON()
  const keys = json.keys ?? {}
  if (!json.endpoint || !keys.p256dh || !keys.auth) {
    throw new Error(
      'This browser produced a push subscription with no keys on it, so there is nothing the ' +
        'Worker could encrypt to. Nothing was saved.',
    )
  }

  return api.subscribeToPush({
    endpoint: json.endpoint,
    keys: { p256dh: keys.p256dh, auth: keys.auth },
    ...(label?.trim() ? { label: label.trim() } : {}),
    ...(typeof navigator !== 'undefined' && navigator.userAgent
      ? { userAgent: navigator.userAgent }
      : {}),
  })
}

/**
 * TURN THIS DEVICE OFF — both halves, and the server half first.
 *
 * Order matters on failure: if the API call fails after the browser has already unsubscribed,
 * the row would keep receiving pushes this browser can no longer decrypt, and the screen would
 * have no endpoint left to match it with. Failing before anything changed is recoverable; the
 * other way round is not.
 *
 * A browser with no subscription is not an error. It is already off.
 */
export async function unsubscribe(api: PushApi): Promise<void> {
  const endpoint = await browserEndpoint()
  if (endpoint) {
    const devices = await api.getPushSubscriptions()
    const device = devices.find((d) => d.endpoint === endpoint)
    if (device) await api.unsubscribeFromPush(device.id)
  }
  if (!pushSupported()) return
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  if (subscription) await subscription.unsubscribe()
  await forgetApplicationServerKey()
}

// ── The handoff with the service worker ─────────────────────────────────────

/**
 * POST A SUBSCRIPTION THE SERVICE WORKER RENEWED WHILE NOBODY WAS LOOKING.
 *
 * `pushsubscriptionchange` fires with no page open and no JWT anywhere in scope, so push-sw.js
 * re-subscribes and leaves the result in the cache. This runs on app open, sends it with a real
 * token, and clears the slot. Returns null when there was nothing waiting, which is the ordinary
 * case — this is a no-op on almost every load.
 *
 * IT NEVER THROWS AT THE CALLER. A failure here means the renewal is still in the cache and the
 * next open tries again; turning that into an error boundary on the Notifications screen would
 * be a worse answer than quietly retrying.
 */
export async function flushPendingResubscription(api: PushApi): Promise<PushDevice | null> {
  if (typeof caches === 'undefined') return null
  try {
    const cache = await caches.open(PUSH_CACHE)
    const stored = await cache.match(PUSH_PENDING_URL)
    if (!stored) return null
    const json = (await stored.json()) as {
      endpoint?: string
      keys?: { p256dh?: string; auth?: string }
    }
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
      // Unusable. Dropping it is right: it cannot be sent, and keeping it would mean retrying a
      // thing that can never succeed on every single app open.
      await cache.delete(PUSH_PENDING_URL)
      return null
    }
    const device = await api.subscribeToPush({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      ...(typeof navigator !== 'undefined' && navigator.userAgent
        ? { userAgent: navigator.userAgent }
        : {}),
    })
    await cache.delete(PUSH_PENDING_URL)
    return device
  } catch {
    return null
  }
}

// The key as `base64UrlToBytes` produced it — ArrayBuffer-backed, which is what makes it a
// `BodyInit` at all. Widening this to a bare `Uint8Array` is what broke the build once.
async function stashApplicationServerKey(
  keyBytes: ReturnType<typeof base64UrlToBytes>,
): Promise<void> {
  if (typeof caches === 'undefined') return
  try {
    const cache = await caches.open(PUSH_CACHE)
    // The BYTES, not the base64url string: the service worker hands this straight to
    // `pushManager.subscribe` as a BufferSource, so the decoding happens once, here, where there
    // is a test for it.
    await cache.put(PUSH_KEY_URL, new Response(keyBytes))
  } catch {
    // A browser that refuses cache storage still subscribes; it just cannot renew unattended,
    // and the next app open re-subscribes it. Not worth failing the press over.
  }
}

async function forgetApplicationServerKey(): Promise<void> {
  if (typeof caches === 'undefined') return
  try {
    const cache = await caches.open(PUSH_CACHE)
    await cache.delete(PUSH_KEY_URL)
    await cache.delete(PUSH_PENDING_URL)
  } catch {
    /* nothing to do, and nothing that depends on it */
  }
}
